import { useState, useEffect, useRef, useCallback } from 'react';
import mapsOptimizer from '../../../utils/mapsOptimizer';
import mapsCache from '../../../utils/mapsCache';
import { buildVenuePath } from '../../../utils/venueSlug';

export const useMap = (results, localSearchData, navigate, isMobile, viewMode) => {
  const [map, setMap] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState(null);
  
  const mapElementRef = useRef(null);
  const mapInitializingRef = useRef(false);
  const prevLocationRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const hasApiKeyRef = useRef(null);
  
  // Check API key once on mount
  useEffect(() => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyBbFKw4YT2iIg1uPrTcX04ADwQvlwBtbM0';
    hasApiKeyRef.current = !!(apiKey && apiKey.trim() !== '');
    if (!hasApiKeyRef.current) {
      setMapError('Unable to load map. Please try again later.');
    }
  }, []);

  // Sync refs with state
  useEffect(() => {
    mapRef.current = map;
  }, [map]);

  useEffect(() => {
    markersRef.current = markers;
  }, [markers]);

  // Initialize maps optimizer on component mount (only if API key exists)
  useEffect(() => {
    if (hasApiKeyRef.current === false) {
      return;
    }

    const initializeMaps = async () => {
      try {
        await mapsOptimizer.initialize();
      } catch (error) {
        if (error.message && error.message.includes('API key')) {
          hasApiKeyRef.current = false;
          setMapError('Unable to load map. Please try again later.');
        } else {
          setMapError('Map is temporarily unavailable. Please refresh the page.');
        }
      }
    };

    initializeMaps();

    return () => {
      try {
        setMap(null);
        setMarkers([]);
        setMapLoading(false);
        mapInitializingRef.current = false;
        mapsOptimizer.cleanup();
      } catch (error) {
        // Silent cleanup
      }
    };
  }, []);

  const getCurrentPosition = () => {
    return new Promise((resolve, reject) => {
      try {
        if (!navigator || !navigator.geolocation) {
          reject(new Error('Geolocation is not supported by this browser.'));
          return;
        }

        navigator.geolocation.getCurrentPosition(
          (position) => resolve(position),
          (error) => reject(error),
          {
            enableHighAccuracy: true,
            timeout: 15000,
            maximumAge: 300000
          }
        );
      } catch (error) {
        reject(error);
      }
    });
  };

  const initializeOptimizedMap = useCallback(async (forceReinitialize = false) => {
    // Early return if no API key
    if (hasApiKeyRef.current === false) {
      return;
    }

    if (!mapElementRef.current || results.length === 0) {
      return;
    }

    if (!mapElementRef.current.parentNode) {
      return;
    }

    if (!forceReinitialize && (mapInitializingRef.current ||
        (mapElementRef.current && mapElementRef.current.dataset.mapInitialized === 'true'))) {
      return;
    }

    if (forceReinitialize && mapElementRef.current) {
      if (mapRef.current) {
        try {
          markersRef.current.forEach(marker => {
            if (marker && marker.setMap) {
              marker.setMap(null);
            }
          });
        } catch (error) {
          // Silent cleanup
        }
        setMap(null);
        setMarkers([]);
      }

      mapElementRef.current.dataset.mapInitialized = 'false';
      mapInitializingRef.current = false;

      if (mapElementRef.current && mapElementRef.current.parentNode) {
        try {
          mapElementRef.current.innerHTML = '';
        } catch (error) {
          // Silent cleanup
        }
      }
    }

    mapInitializingRef.current = true;
    setMapLoading(true);
    setMapError(null);

    try {
      await mapsCache.waitForGoogleMaps(15000);

      if (!window.google || !window.google.maps) {
        const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyBbFKw4YT2iIg1uPrTcX04ADwQvlwBtbM0';
        if (!apiKey || apiKey.trim() === '') {
          setMapError('Unable to load map. Please try again later.');
        } else {
          setMapError('We\'re having trouble loading the map. Please check your internet connection and try again.');
        }
        setMapLoading(false);
        mapInitializingRef.current = false;
        return;
      }

      if (!mapsOptimizer.isInitialized) {
        await mapsOptimizer.initialize();
      }

      const currentLocation = localSearchData.location || '';
      const currentDate = localSearchData.date || '';
      const currentEventType = localSearchData.eventType || '';
      const currentVenueBriefRequestId = localSearchData.venueBriefRequestId || '';
      
      const venuesWithHandlers = results.map(venue => ({
        ...venue,
        onClick: (clickedVenue) => {
          const searchParams = new URLSearchParams();
          if (currentLocation) searchParams.set('location', currentLocation);
          if (currentDate) searchParams.set('date', currentDate);
          if (currentEventType) searchParams.set('eventType', currentEventType);
          if (currentVenueBriefRequestId) {
            searchParams.set('venueBriefRequestId', currentVenueBriefRequestId);
          }
          navigate(buildVenuePath(clickedVenue, { searchParams }), {
            state: { venueSummary: clickedVenue }
          });
        }
      }));

      let mapCenter = { lat: 51.5074, lng: -0.1278 }; // Default to London

      if (currentLocation && currentLocation.toLowerCase() === 'current location') {
        try {
          const position = await getCurrentPosition();
          mapCenter = { lat: position.coords.latitude, lng: position.coords.longitude };
        } catch (error) {
          if (results.length > 0) {
            mapCenter = { lat: results[0].coordinates.lat, lng: results[0].coordinates.lng };
          }
        }
      } else if (currentLocation && currentLocation.toLowerCase() === 'austin') {
        mapCenter = { lat: 30.2672, lng: -97.7431 };
      } else if (currentLocation && results.length > 0) {
        mapCenter = { lat: results[0].coordinates.lat, lng: results[0].coordinates.lng };
      }

      if (forceReinitialize) {
        try {
          const result = await mapsOptimizer.createMap(mapElementRef.current, {
            center: mapCenter,
            zoom: 13,
            venues: venuesWithHandlers
          }, true);
          
          if (!result) {
            setMapError('Unable to display map. Please try again.');
            setMapLoading(false);
            mapInitializingRef.current = false;
            return;
          }
          
          const { map: mapInstance, markers: mapMarkers } = result;
          
          if (mapInstance) {
            setMap(mapInstance);
            setMarkers(mapMarkers);
          }
          
          setMapLoading(false);
          mapInitializingRef.current = false;
        } catch (error) {
          setMapError('Unable to display map. Please try again.');
          setMapLoading(false);
          mapInitializingRef.current = false;
        }
      } else {
        mapsOptimizer.observeMapElement(
          mapElementRef.current,
          {
            center: mapCenter,
            zoom: 13,
            venues: venuesWithHandlers
          },
          async (mapElement, options) => {
            try {
              if (!mapElement || !mapElement.parentNode) {
                setMapLoading(false);
                return;
              }

              const result = await mapsOptimizer.createMap(mapElement, options);
              
              if (!result) {
                setMapError('Unable to display map. Please try again.');
                setMapLoading(false);
                return;
              }
              
              const { map: mapInstance, markers: mapMarkers } = result;
              
              if (!mapInstance) {
                setMapLoading(false);
                return;
              }
              
              setMap(mapInstance);
              setMarkers(mapMarkers);
              mapsOptimizer.optimizeForMobile(mapInstance);
              setMapLoading(false);
            } catch (error) {
              setMapError('We\'re having trouble loading the map. Please try again.');
              setMapLoading(false);
            } finally {
              mapInitializingRef.current = false;
            }
          }
        );
      }
    } catch (error) {
      let errorMessage = 'Unable to load map. Please try again.';
      if (error.message && error.message.includes('API key')) {
        errorMessage = 'Unable to load map. Please try again later.';
        hasApiKeyRef.current = false;
      }
      setMapError(errorMessage);
      setMapLoading(false);
      mapInitializingRef.current = false;
    }
  }, [
    results,
    navigate,
    localSearchData.location,
    localSearchData.date,
    localSearchData.eventType,
    localSearchData.venueBriefRequestId
  ]);

  // Reinitialize map when switching to map view on mobile/tablet
  useEffect(() => {
    if (hasApiKeyRef.current === false) {
      return;
    }
    
    if (viewMode === 'map' && isMobile && results.length > 0 && mapElementRef.current) {
      // Only initialize if not already initialized
      if (mapElementRef.current.dataset.mapInitialized === 'true') {
        return;
      }
      
      const timer = setTimeout(() => {
        if (mapElementRef.current && !mapInitializingRef.current) {
          initializeOptimizedMap(true);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [viewMode, isMobile, results.length, initializeOptimizedMap]);

  // Initialize or re-initialize map when results or location change
  useEffect(() => {
    if (hasApiKeyRef.current === false) {
      return;
    }
    
    if (results.length === 0 || !mapElementRef.current) {
      return;
    }
    
    const shouldShowMap = !isMobile || viewMode === 'map';
    
    if (!shouldShowMap) {
      return;
    }
    
    // Prevent multiple initializations
    if (mapInitializingRef.current) {
      return;
    }
    
    // Check if already initialized (unless location changed)
    const currentLocation = localSearchData.location || '';
    const locationChanged = prevLocationRef.current !== currentLocation;
    
    if (!locationChanged && mapElementRef.current.dataset.mapInitialized === 'true') {
      return;
    }
    
    // Update location ref immediately to prevent duplicate calls
    prevLocationRef.current = currentLocation;
    
    const timer = setTimeout(() => {
      if (mapElementRef.current && !mapInitializingRef.current && hasApiKeyRef.current !== false) {
        initializeOptimizedMap(locationChanged);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [results.length, localSearchData.location, viewMode, isMobile, initializeOptimizedMap]);

  // Cleanup map and markers
  useEffect(() => {
    return () => {
      try {
        if (map) {
          markers.forEach(marker => {
            if (marker && marker.setMap) {
              marker.setMap(null);
            }
          });
        }
      } catch (error) {
        // Silent cleanup
      }
    };
  }, [map, markers]);

  return {
    mapElementRef,
    mapInstance: map,
    mapLoading,
    mapError,
    initializeOptimizedMap
  };
};

